import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { awardXP } from '../utils/xp';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './Sentences.css';

// ---------------------------------------------------------------------------
// Names of the five languages, written in each interface language
// ---------------------------------------------------------------------------
const LANGUAGE_NAMES = {
    en: { english: 'English', italian: 'Italian', spanish: 'Spanish', french: 'French', portuguese: 'Portuguese' },
    it: { english: 'inglese', italian: 'italiano', spanish: 'spagnolo', french: 'francese', portuguese: 'portoghese' },
    es: { english: 'inglés', italian: 'italiano', spanish: 'español', french: 'francés', portuguese: 'portugués' },
    fr: { english: 'anglais', italian: 'italien', spanish: 'espagnol', french: 'français', portuguese: 'portugais' },
    pt: { english: 'inglês', italian: 'italiano', spanish: 'espanhol', french: 'francês', portuguese: 'português' },
};

// English names, used when talking to the AI reviewer
const ENGLISH_NAMES = {
    en: 'English', it: 'Italian', es: 'Spanish', fr: 'French', pt: 'Portuguese',
    english: 'English', italian: 'Italian', spanish: 'Spanish', french: 'French', portuguese: 'Portuguese',
};

// ---------------------------------------------------------------------------
// Screen text
// ---------------------------------------------------------------------------
const TEXT = {
    en: {
        pickerTitle: 'Practice writing in {language}',
        pickerSub: 'Get detailed feedback on your grammar and word choice',
        chooseTopic: 'Choose a topic',
        shortBtn: 'Short (2-3 sentences)',
        longBtn: 'Long (a paragraph)',
        orGrammar: 'Or practice a grammar point',
        grammarPractice: 'Grammar practice',
        grammarPracticeDesc: 'Focus your writing on one grammar structure',
        chooseGrammar: 'Choose a grammar topic',
        typeOwn: 'Type your own topic...',
        short: 'Short',
        long: 'Long',
        chooseAnother: 'Choose another',
        example: 'Example:',
        paragraph: 'Paragraph',
        grammarFocus: '📐 Grammar focus',
        yourPrompt: '✏️ Your prompt',
        placeholder: 'Write here in {language}...',
        check: 'Check my writing →',
        checking: 'Checking your {language}...',
        error: 'Something went wrong checking your writing. Please try again.',
        tryAgainBtn: 'Try again',
        youWrote: 'What you wrote',
        allCorrect: '🎉 Great job! No issues found.',
        corrections: 'Corrections',
        didWell: 'What you did well',
        newPrompt: 'New prompt →',
        tryAgain: 'Try again →',
        grammarPrompt: 'Write {count} in {language} using {topic}.',
        count2: '2-3 sentences',
        count5: '5-7 sentences',
    },
    it: {
        pickerTitle: 'Esercitati a scrivere in {language}',
        pickerSub: 'Ricevi un riscontro dettagliato su grammatica e scelta delle parole',
        chooseTopic: 'Scegli un argomento',
        shortBtn: 'Breve (2-3 frasi)',
        longBtn: 'Lungo (un paragrafo)',
        orGrammar: 'Oppure esercita un punto di grammatica',
        grammarPractice: 'Esercizio di grammatica',
        grammarPracticeDesc: 'Concentra la scrittura su una struttura grammaticale',
        chooseGrammar: 'Scegli un argomento di grammatica',
        typeOwn: 'Scrivi un argomento tuo...',
        short: 'Breve',
        long: 'Lungo',
        chooseAnother: 'Scegline un altro',
        example: 'Esempio:',
        paragraph: 'Paragrafo',
        grammarFocus: '📐 Focus grammaticale',
        yourPrompt: '✏️ La tua traccia',
        placeholder: 'Scrivi qui in {language}...',
        check: 'Controlla il mio testo →',
        checking: 'Controllo il tuo testo in {language}...',
        error: 'Qualcosa è andato storto durante il controllo. Riprova.',
        tryAgainBtn: 'Riprova',
        youWrote: 'Cosa hai scritto',
        allCorrect: '🎉 Ottimo lavoro! Nessun errore trovato.',
        corrections: 'Correzioni',
        didWell: 'Cosa hai fatto bene',
        newPrompt: 'Nuova traccia →',
        tryAgain: 'Riprova →',
        grammarPrompt: 'Scrivi {count} in {language} usando {topic}.',
        count2: '2-3 frasi',
        count5: '5-7 frasi',
    },
    es: {
        pickerTitle: 'Practica la escritura en {language}',
        pickerSub: 'Recibe comentarios detallados sobre tu gramática y tu elección de palabras',
        chooseTopic: 'Elige un tema',
        shortBtn: 'Corto (2-3 oraciones)',
        longBtn: 'Largo (un párrafo)',
        orGrammar: 'O practica un punto de gramática',
        grammarPractice: 'Práctica de gramática',
        grammarPracticeDesc: 'Centra tu escritura en una estructura gramatical',
        chooseGrammar: 'Elige un tema de gramática',
        typeOwn: 'Escribe tu propio tema...',
        short: 'Corto',
        long: 'Largo',
        chooseAnother: 'Elegir otro',
        example: 'Ejemplo:',
        paragraph: 'Párrafo',
        grammarFocus: '📐 Enfoque gramatical',
        yourPrompt: '✏️ Tu consigna',
        placeholder: 'Escribe aquí en {language}...',
        check: 'Revisar mi texto →',
        checking: 'Revisando tu texto en {language}...',
        error: 'Algo salió mal al revisar tu texto. Inténtalo de nuevo.',
        tryAgainBtn: 'Intentar de nuevo',
        youWrote: 'Lo que escribiste',
        allCorrect: '🎉 ¡Buen trabajo! No se encontraron errores.',
        corrections: 'Correcciones',
        didWell: 'Lo que hiciste bien',
        newPrompt: 'Nueva consigna →',
        tryAgain: 'Intentar de nuevo →',
        grammarPrompt: 'Escribe {count} en {language} usando {topic}.',
        count2: '2-3 oraciones',
        count5: '5-7 oraciones',
    },
    fr: {
        pickerTitle: 'Entraîne-toi à écrire en {language}',
        pickerSub: 'Reçois des retours détaillés sur ta grammaire et ton choix de mots',
        chooseTopic: 'Choisis un sujet',
        shortBtn: 'Court (2 à 3 phrases)',
        longBtn: 'Long (un paragraphe)',
        orGrammar: 'Ou travaille un point de grammaire',
        grammarPractice: 'Exercice de grammaire',
        grammarPracticeDesc: 'Concentre ton écriture sur une structure grammaticale',
        chooseGrammar: 'Choisis un point de grammaire',
        typeOwn: 'Écris ton propre sujet...',
        short: 'Court',
        long: 'Long',
        chooseAnother: 'En choisir un autre',
        example: 'Exemple :',
        paragraph: 'Paragraphe',
        grammarFocus: '📐 Point de grammaire',
        yourPrompt: '✏️ Ta consigne',
        placeholder: 'Écris ici en {language}...',
        check: 'Corriger mon texte →',
        checking: 'Correction de ton texte en {language}...',
        error: 'Une erreur s’est produite pendant la correction. Réessaie.',
        tryAgainBtn: 'Réessayer',
        youWrote: 'Ce que tu as écrit',
        allCorrect: '🎉 Bravo ! Aucune erreur trouvée.',
        corrections: 'Corrections',
        didWell: 'Ce que tu as bien fait',
        newPrompt: 'Nouvelle consigne →',
        tryAgain: 'Réessayer →',
        grammarPrompt: 'Écris {count} en {language} en utilisant {topic}.',
        count2: '2 à 3 phrases',
        count5: '5 à 7 phrases',
    },
    pt: {
        pickerTitle: 'Pratique a escrita em {language}',
        pickerSub: 'Receba um retorno detalhado sobre sua gramática e escolha de palavras',
        chooseTopic: 'Escolha um tema',
        shortBtn: 'Curto (2-3 frases)',
        longBtn: 'Longo (um parágrafo)',
        orGrammar: 'Ou pratique um ponto de gramática',
        grammarPractice: 'Prática de gramática',
        grammarPracticeDesc: 'Concentre sua escrita em uma estrutura gramatical',
        chooseGrammar: 'Escolha um tema de gramática',
        typeOwn: 'Digite seu próprio tema...',
        short: 'Curto',
        long: 'Longo',
        chooseAnother: 'Escolher outro',
        example: 'Exemplo:',
        paragraph: 'Parágrafo',
        grammarFocus: '📐 Foco gramatical',
        yourPrompt: '✏️ Sua proposta',
        placeholder: 'Escreva aqui em {language}...',
        check: 'Corrigir meu texto →',
        checking: 'Corrigindo seu texto em {language}...',
        error: 'Algo deu errado ao corrigir seu texto. Tente novamente.',
        tryAgainBtn: 'Tentar novamente',
        youWrote: 'O que você escreveu',
        allCorrect: '🎉 Ótimo trabalho! Nenhum erro encontrado.',
        corrections: 'Correções',
        didWell: 'O que você fez bem',
        newPrompt: 'Nova proposta →',
        tryAgain: 'Tentar novamente →',
        grammarPrompt: 'Escreva {count} em {language} usando {topic}.',
        count2: '2-3 frases',
        count5: '5-7 frases',
    },
};

// ---------------------------------------------------------------------------
// Writing prompts, written in the interface language. The learner answers in the language they study.
// The lists have the same order in every language.
// ---------------------------------------------------------------------------
const PROMPTS = {
    en: {
        short: [
            'Describe your favorite food.',
            'Write about what you did yesterday.',
            'Describe your best friend.',
            'What is your morning routine?',
            'Write about your family.',
            'Describe the place where you live.',
            'What do you like to do on weekends?',
            'Write about your favorite season and why you like it.',
            'Describe what you are wearing today.',
            'Write about a trip you would like to take.',
            'What is your job, or what would your dream job be?',
            'Describe your favorite room at home.',
            'Write about the weather today.',
            'What did you eat today?',
            'Write about a hobby you enjoy.',
            'Describe your last holiday.',
            'Introduce yourself to a new neighbor.',
            'Write about your plans for next week.',
        ],
        long: [
            'Write about a memorable day in your life and explain why it was special.',
            'Describe your city or town to someone who has never visited it.',
            'Write about a person who has influenced you and explain how.',
            'Describe your ideal weekend from morning to night.',
            'Write about a problem you solved recently and how you did it.',
            'Tell the story of a trip, real or imagined, from beginning to end.',
            'Explain how you would change one thing in your daily life, and why.',
            'Write about a book, film or song you like and why you recommend it.',
            'Describe a typical day in your life, from waking up to going to sleep.',
            'Write about what you would do if you won a large prize.',
        ],
    },
    it: {
        short: [
            'Descrivi il tuo cibo preferito.',
            'Scrivi cosa hai fatto ieri.',
            'Descrivi il tuo migliore amico o la tua migliore amica.',
            'Com’è la tua routine del mattino?',
            'Scrivi della tua famiglia.',
            'Descrivi il posto in cui vivi.',
            'Cosa ti piace fare nel fine settimana?',
            'Scrivi della tua stagione preferita e di perché ti piace.',
            'Descrivi cosa indossi oggi.',
            'Scrivi di un viaggio che vorresti fare.',
            'Che lavoro fai, o quale sarebbe il lavoro dei tuoi sogni?',
            'Descrivi la tua stanza preferita di casa.',
            'Scrivi del tempo che fa oggi.',
            'Cosa hai mangiato oggi?',
            'Scrivi di un hobby che ti piace.',
            'Descrivi la tua ultima vacanza.',
            'Presentati a un nuovo vicino di casa.',
            'Scrivi dei tuoi programmi per la prossima settimana.',
        ],
        long: [
            'Scrivi di un giorno indimenticabile della tua vita e spiega perché è stato speciale.',
            'Descrivi la tua città o il tuo paese a chi non l’ha mai visitato.',
            'Scrivi di una persona che ti ha influenzato e spiega come.',
            'Descrivi il tuo fine settimana ideale dalla mattina alla sera.',
            'Scrivi di un problema che hai risolto di recente e di come ci sei riuscito.',
            'Racconta la storia di un viaggio, vero o immaginario, dall’inizio alla fine.',
            'Spiega cosa cambieresti nella tua vita quotidiana e perché.',
            'Scrivi di un libro, un film o una canzone che ti piace e di perché lo consigli.',
            'Descrivi una tua giornata tipo, dal risveglio fino a quando vai a dormire.',
            'Scrivi cosa faresti se vincessi un premio molto grande.',
        ],
    },
    es: {
        short: [
            'Describe tu comida favorita.',
            'Escribe lo que hiciste ayer.',
            'Describe a tu mejor amigo o amiga.',
            '¿Cómo es tu rutina de la mañana?',
            'Escribe sobre tu familia.',
            'Describe el lugar donde vives.',
            '¿Qué te gusta hacer los fines de semana?',
            'Escribe sobre tu estación favorita y por qué te gusta.',
            'Describe lo que llevas puesto hoy.',
            'Escribe sobre un viaje que te gustaría hacer.',
            '¿A qué te dedicas, o cuál sería tu trabajo ideal?',
            'Describe tu habitación favorita de la casa.',
            'Escribe sobre el tiempo que hace hoy.',
            '¿Qué comiste hoy?',
            'Escribe sobre una afición que disfrutas.',
            'Describe tus últimas vacaciones.',
            'Preséntate a un vecino nuevo.',
            'Escribe sobre tus planes para la próxima semana.',
        ],
        long: [
            'Escribe sobre un día inolvidable de tu vida y explica por qué fue especial.',
            'Describe tu ciudad o tu pueblo a alguien que nunca lo ha visitado.',
            'Escribe sobre una persona que te ha influido y explica cómo.',
            'Describe tu fin de semana ideal de la mañana a la noche.',
            'Escribe sobre un problema que resolviste hace poco y cómo lo lograste.',
            'Cuenta la historia de un viaje, real o imaginario, de principio a fin.',
            'Explica qué cambiarías de tu vida diaria y por qué.',
            'Escribe sobre un libro, una película o una canción que te gusta y por qué la recomiendas.',
            'Describe un día típico de tu vida, desde que te levantas hasta que te acuestas.',
            'Escribe qué harías si ganaras un premio muy grande.',
        ],
    },
    fr: {
        short: [
            'Décris ton plat préféré.',
            'Écris ce que tu as fait hier.',
            'Décris ton meilleur ami ou ta meilleure amie.',
            'À quoi ressemble ta routine du matin ?',
            'Écris sur ta famille.',
            'Décris l’endroit où tu habites.',
            'Qu’aimes-tu faire le week-end ?',
            'Écris sur ta saison préférée et explique pourquoi tu l’aimes.',
            'Décris ce que tu portes aujourd’hui.',
            'Écris sur un voyage que tu aimerais faire.',
            'Quel est ton métier, ou quel serait ton métier de rêve ?',
            'Décris ta pièce préférée de la maison.',
            'Écris sur le temps qu’il fait aujourd’hui.',
            'Qu’as-tu mangé aujourd’hui ?',
            'Écris sur un loisir que tu aimes.',
            'Décris tes dernières vacances.',
            'Présente-toi à un nouveau voisin.',
            'Écris sur tes projets pour la semaine prochaine.',
        ],
        long: [
            'Écris sur un jour inoubliable de ta vie et explique pourquoi il était spécial.',
            'Décris ta ville ou ton village à quelqu’un qui ne l’a jamais visité.',
            'Écris sur une personne qui t’a influencé et explique comment.',
            'Décris ton week-end idéal, du matin au soir.',
            'Écris sur un problème que tu as résolu récemment et comment tu y es arrivé.',
            'Raconte l’histoire d’un voyage, réel ou imaginaire, du début à la fin.',
            'Explique ce que tu changerais dans ta vie quotidienne et pourquoi.',
            'Écris sur un livre, un film ou une chanson que tu aimes et pourquoi tu le recommandes.',
            'Décris une journée type de ta vie, du réveil au coucher.',
            'Écris ce que tu ferais si tu gagnais un très gros prix.',
        ],
    },
    pt: {
        short: [
            'Descreva sua comida favorita.',
            'Escreva o que você fez ontem.',
            'Descreva seu melhor amigo ou sua melhor amiga.',
            'Como é a sua rotina da manhã?',
            'Escreva sobre a sua família.',
            'Descreva o lugar onde você mora.',
            'O que você gosta de fazer nos fins de semana?',
            'Escreva sobre a sua estação favorita e por que você gosta dela.',
            'Descreva o que você está vestindo hoje.',
            'Escreva sobre uma viagem que você gostaria de fazer.',
            'Qual é a sua profissão, ou qual seria o seu emprego dos sonhos?',
            'Descreva o seu cômodo favorito da casa.',
            'Escreva sobre o tempo hoje.',
            'O que você comeu hoje?',
            'Escreva sobre um hobby de que você gosta.',
            'Descreva as suas últimas férias.',
            'Apresente-se a um novo vizinho.',
            'Escreva sobre os seus planos para a próxima semana.',
        ],
        long: [
            'Escreva sobre um dia inesquecível da sua vida e explique por que ele foi especial.',
            'Descreva a sua cidade a alguém que nunca a visitou.',
            'Escreva sobre uma pessoa que influenciou você e explique como.',
            'Descreva o seu fim de semana ideal, da manhã à noite.',
            'Escreva sobre um problema que você resolveu recentemente e como conseguiu.',
            'Conte a história de uma viagem, real ou imaginária, do começo ao fim.',
            'Explique o que você mudaria na sua vida diária e por quê.',
            'Escreva sobre um livro, filme ou música de que você gosta e por que o recomenda.',
            'Descreva um dia típico da sua vida, de quando acorda até a hora de dormir.',
            'Escreva o que você faria se ganhasse um prêmio enorme.',
        ],
    },
};

// ---------------------------------------------------------------------------
// Grammar topics. Each concept has one explanation per interface language.
// Each learning language lists its own topics, with the topic name and an example in that language.
// ---------------------------------------------------------------------------
const CONCEPTS = {
    pastDone: {
        en: 'Used for completed actions in the past.',
        it: 'Si usa per azioni concluse nel passato.',
        es: 'Se usa para acciones terminadas en el pasado.',
        fr: 'Sert à parler d’actions terminées dans le passé.',
        pt: 'Usa-se para ações concluídas no passado.',
    },
    pastOngoing: {
        en: 'Used for ongoing or repeated past actions and for descriptions.',
        it: 'Si usa per azioni passate in corso o ripetute e per le descrizioni.',
        es: 'Se usa para acciones pasadas en curso o repetidas y para descripciones.',
        fr: 'Sert à parler d’actions passées en cours ou répétées et à décrire.',
        pt: 'Usa-se para ações passadas em andamento ou repetidas e para descrições.',
    },
    future: {
        en: 'Used for future plans and predictions.',
        it: 'Si usa per progetti e previsioni future.',
        es: 'Se usa para planes y predicciones futuras.',
        fr: 'Sert à parler de projets et de prévisions futurs.',
        pt: 'Usa-se para planos e previsões futuras.',
    },
    conditional: {
        en: 'Used for polite requests, wishes and hypotheses.',
        it: 'Si usa per richieste cortesi, desideri e ipotesi.',
        es: 'Se usa para peticiones corteses, deseos e hipótesis.',
        fr: 'Sert aux demandes polies, aux souhaits et aux hypothèses.',
        pt: 'Usa-se para pedidos educados, desejos e hipóteses.',
    },
    subjunctive: {
        en: 'Used after opinions, doubts, wishes and emotions.',
        it: 'Si usa dopo opinioni, dubbi, desideri ed emozioni.',
        es: 'Se usa después de opiniones, dudas, deseos y emociones.',
        fr: 'S’utilise après les opinions, les doutes, les souhaits et les émotions.',
        pt: 'Usa-se depois de opiniões, dúvidas, desejos e emoções.',
    },
    ifClauses: {
        en: 'Used to talk about real and imagined situations with “if”.',
        it: 'Si usa per parlare di situazioni reali e immaginarie con “if”.',
        es: 'Se usa para hablar de situaciones reales e imaginarias con “if”.',
        fr: 'Sert à parler de situations réelles et imaginaires avec « if ».',
        pt: 'Usa-se para falar de situações reais e imaginadas com “if”.',
    },
    progressive: {
        en: 'Used for actions happening right now.',
        it: 'Si usa per azioni che stanno avvenendo ora.',
        es: 'Se usa para acciones que ocurren ahora mismo.',
        fr: 'Sert à parler d’actions qui se passent en ce moment.',
        pt: 'Usa-se para ações que estão acontecendo agora.',
    },
    directPron: {
        en: 'Direct object pronouns replace the noun that receives the action.',
        it: 'I pronomi diretti sostituiscono il nome che riceve l’azione.',
        es: 'Los pronombres de objeto directo sustituyen al nombre que recibe la acción.',
        fr: 'Les pronoms compléments d’objet direct remplacent le nom qui reçoit l’action.',
        pt: 'Os pronomes de objeto direto substituem o nome que recebe a ação.',
    },
    indirectPron: {
        en: 'Indirect object pronouns stand for the person who receives or benefits from the action.',
        it: 'I pronomi indiretti indicano la persona che riceve l’azione o ne trae beneficio.',
        es: 'Los pronombres de objeto indirecto indican la persona que recibe la acción o se beneficia de ella.',
        fr: 'Les pronoms compléments d’objet indirect désignent la personne qui reçoit l’action ou en bénéficie.',
        pt: 'Os pronomes de objeto indireto indicam a pessoa que recebe a ação ou se beneficia dela.',
    },
    prepositions: {
        en: 'Prepositions show relationships such as place, time and direction.',
        it: 'Le preposizioni indicano relazioni come luogo, tempo e direzione.',
        es: 'Las preposiciones indican relaciones como lugar, tiempo y dirección.',
        fr: 'Les prépositions expriment des relations comme le lieu, le temps et la direction.',
        pt: 'As preposições indicam relações como lugar, tempo e direção.',
    },
    comparatives: {
        en: 'Used to compare things: more, less, as much as, the most.',
        it: 'Si usa per confrontare cose: più, meno, tanto quanto, il più.',
        es: 'Se usa para comparar cosas: más, menos, tan como, el más.',
        fr: 'Sert à comparer : plus, moins, aussi que, le plus.',
        pt: 'Usa-se para comparar coisas: mais, menos, tão quanto, o mais.',
    },
    imperative: {
        en: 'Used to give commands, instructions and advice.',
        it: 'Si usa per dare ordini, istruzioni e consigli.',
        es: 'Se usa para dar órdenes, instrucciones y consejos.',
        fr: 'Sert à donner des ordres, des instructions et des conseils.',
        pt: 'Usa-se para dar ordens, instruções e conselhos.',
    },
    ciNe: {
        en: '“Ci” replaces a place; “ne” replaces a quantity or “of it/of them”.',
        it: '“Ci” sostituisce un luogo; “ne” sostituisce una quantità o “di questo/di questi”.',
        es: '“Ci” sustituye un lugar; “ne” sustituye una cantidad o “de eso/de ellos”.',
        fr: '« Ci » remplace un lieu ; « ne » remplace une quantité ou « de cela/d’eux ».',
        pt: '“Ci” substitui um lugar; “ne” substitui uma quantidade ou “disso/deles”.',
    },
    siImpersonale: {
        en: 'The impersonal form for “one” or “people” in general.',
        it: 'La forma impersonale che indica “la gente” in senso generale.',
        es: 'La forma impersonal que equivale a “uno” o “la gente” en general.',
        fr: 'La forme impersonnelle qui correspond à « on » ou « les gens » en général.',
        pt: 'A forma impersonal que equivale a “a gente” ou “as pessoas” em geral.',
    },
    serEstar: {
        en: 'Two verbs for “to be”: ser for identity and lasting traits, estar for states and locations.',
        it: 'Due verbi per “essere”: ser per identità e caratteristiche durature, estar per stati e luoghi.',
        es: 'Dos verbos para “ser”: ser para la identidad y los rasgos duraderos, estar para estados y ubicaciones.',
        fr: 'Deux verbes pour « être » : ser pour l’identité et les traits durables, estar pour les états et les lieux.',
        pt: 'Dois verbos para “ser/estar”: ser indica identidade e características duradouras; estar indica estados e lugares.',
    },
    porPara: {
        en: '“Por” gives a cause or means; “para” gives a purpose, goal or destination.',
        it: '“Por” indica causa o mezzo; “para” indica scopo, meta o destinazione.',
        es: '“Por” indica causa o medio; “para” indica finalidad, meta o destino.',
        fr: '« Por » indique la cause ou le moyen ; « para » indique le but ou la destination.',
        pt: '“Por” indica causa ou meio; “para” indica finalidade, meta ou destino.',
    },
    yEn: {
        en: '“Y” replaces a place or “à + thing”; “en” replaces a quantity or “de + thing”.',
        it: '“Y” sostituisce un luogo o “à + cosa”; “en” sostituisce una quantità o “de + cosa”.',
        es: '“Y” sustituye un lugar o “à + cosa”; “en” sustituye una cantidad o “de + cosa”.',
        fr: '« Y » remplace un lieu ou « à + chose » ; « en » remplace une quantité ou « de + chose ».',
        pt: '“Y” substitui um lugar ou “à + coisa”; “en” substitui uma quantidade ou “de + coisa”.',
    },
    partitifs: {
        en: 'Partitive articles (du, de la, des) express an unspecified amount.',
        it: 'Gli articoli partitivi (du, de la, des) indicano una quantità non precisata.',
        es: 'Los artículos partitivos (du, de la, des) indican una cantidad no precisada.',
        fr: 'Les articles partitifs (du, de la, des) expriment une quantité non précisée.',
        pt: 'Os artigos partitivos (du, de la, des) indicam uma quantidade não especificada.',
    },
    personalInfinitive: {
        en: 'An infinitive that takes a personal ending to agree with its subject. Unique to Portuguese.',
        it: 'Un infinito che prende una desinenza personale per concordare con il soggetto. È tipico del portoghese.',
        es: 'Un infinitivo que toma una terminación personal para concordar con el sujeto. Es exclusivo del portugués.',
        fr: 'Un infinitif qui prend une terminaison personnelle pour s’accorder avec son sujet. Propre au portugais.',
        pt: 'Um infinitivo que recebe uma terminação pessoal para concordar com o sujeito. Exclusivo do português.',
    },
    presentPerfect: {
        en: 'Links the past to now: experiences, recent events and situations that continue.',
        it: 'Collega il passato al presente: esperienze, eventi recenti e situazioni che continuano.',
        es: 'Une el pasado con el presente: experiencias, hechos recientes y situaciones que continúan.',
        fr: 'Relie le passé au présent : expériences, événements récents et situations qui continuent.',
        pt: 'Liga o passado ao presente: experiências, eventos recentes e situações que continuam.',
    },
    phrasalVerbs: {
        en: 'Verbs combined with a small word, like “give up” or “look for”, that changes the meaning.',
        it: 'Verbi uniti a una piccola parola, come “give up” o “look for”, che ne cambia il significato.',
        es: 'Verbos combinados con una palabra pequeña, como “give up” o “look for”, que cambia su significado.',
        fr: 'Des verbes associés à un petit mot, comme « give up » ou « look for », qui change le sens.',
        pt: 'Verbos combinados com uma palavrinha, como “give up” ou “look for”, que muda o significado.',
    },
};

const GRAMMAR_BY_LANGUAGE = {
    italian: [
        { concept: 'pastDone', name: 'Passato prossimo', example: 'Ieri ho mangiato la pasta.' },
        { concept: 'pastOngoing', name: 'Imperfetto', example: 'Da bambino giocavo sempre fuori.' },
        { concept: 'future', name: 'Futuro semplice', example: 'Domani andrò al lavoro.' },
        { concept: 'conditional', name: 'Condizionale', example: 'Vorrei un caffè.' },
        { concept: 'subjunctive', name: 'Congiuntivo presente', example: 'Penso che sia vero.' },
        { concept: 'progressive', name: 'Stare + gerundio', example: 'Sto mangiando.' },
        { concept: 'directPron', name: 'Pronomi diretti', example: 'Vedi il libro? Lo vedo.' },
        { concept: 'indirectPron', name: 'Pronomi indiretti', example: 'Gli ho scritto una email.' },
        { concept: 'prepositions', name: 'Preposizioni', example: 'Vado a Roma con Marco.' },
        { concept: 'comparatives', name: 'Comparativi e superlativi', example: 'È più alto di me.' },
        { concept: 'imperative', name: 'Imperativo', example: 'Vieni qui!' },
        { concept: 'ciNe', name: 'Ci e ne', example: 'Vai al mare? Sì, ci vado.' },
        { concept: 'siImpersonale', name: 'Si impersonale', example: 'Qui si mangia bene.' },
    ],
    spanish: [
        { concept: 'pastDone', name: 'Pretérito indefinido', example: 'Ayer comí paella.' },
        { concept: 'pastOngoing', name: 'Pretérito imperfecto', example: 'De niño jugaba siempre en la calle.' },
        { concept: 'future', name: 'Futuro simple', example: 'Mañana iré al trabajo.' },
        { concept: 'conditional', name: 'Condicional', example: 'Me gustaría un café.' },
        { concept: 'subjunctive', name: 'Subjuntivo presente', example: 'Espero que vengas mañana.' },
        { concept: 'progressive', name: 'Estar + gerundio', example: 'Estoy comiendo.' },
        { concept: 'directPron', name: 'Pronombres de objeto directo', example: '¿Ves el libro? Sí, lo veo.' },
        { concept: 'indirectPron', name: 'Pronombres de objeto indirecto', example: 'Le escribí un correo.' },
        { concept: 'prepositions', name: 'Preposiciones', example: 'Voy a Madrid con Marta.' },
        { concept: 'comparatives', name: 'Comparativos y superlativos', example: 'Es más alto que yo.' },
        { concept: 'imperative', name: 'Imperativo', example: '¡Ven aquí!' },
        { concept: 'serEstar', name: 'Ser y estar', example: 'Soy italiano, pero estoy en Madrid.' },
        { concept: 'porPara', name: 'Por y para', example: 'Este regalo es para ti.' },
    ],
    french: [
        { concept: 'pastDone', name: 'Passé composé', example: 'Hier, j’ai mangé une crêpe.' },
        { concept: 'pastOngoing', name: 'Imparfait', example: 'Quand j’étais petit, je jouais dehors.' },
        { concept: 'future', name: 'Futur simple', example: 'Demain, j’irai au travail.' },
        { concept: 'conditional', name: 'Conditionnel présent', example: 'Je voudrais un café.' },
        { concept: 'subjunctive', name: 'Subjonctif présent', example: 'Il faut que tu viennes demain.' },
        { concept: 'progressive', name: 'Être en train de', example: 'Je suis en train de manger.' },
        { concept: 'directPron', name: 'Pronoms COD', example: 'Tu vois le livre ? Oui, je le vois.' },
        { concept: 'indirectPron', name: 'Pronoms COI', example: 'Je lui ai écrit un e-mail.' },
        { concept: 'prepositions', name: 'Prépositions', example: 'Je vais à Paris avec Marie.' },
        { concept: 'comparatives', name: 'Comparatifs et superlatifs', example: 'Il est plus grand que moi.' },
        { concept: 'imperative', name: 'Impératif', example: 'Viens ici !' },
        { concept: 'yEn', name: 'Les pronoms y et en', example: 'Tu vas à la plage ? Oui, j’y vais.' },
        { concept: 'partitifs', name: 'Articles partitifs', example: 'Je mange du pain et de la confiture.' },
    ],
    portuguese: [
        { concept: 'pastDone', name: 'Pretérito perfeito', example: 'Ontem comi arroz com feijão.' },
        { concept: 'pastOngoing', name: 'Pretérito imperfeito', example: 'Quando eu era criança, brincava na rua.' },
        { concept: 'future', name: 'Futuro do presente', example: 'Amanhã irei ao trabalho.' },
        { concept: 'conditional', name: 'Futuro do pretérito', example: 'Eu gostaria de um café.' },
        { concept: 'subjunctive', name: 'Presente do subjuntivo', example: 'Espero que você venha amanhã.' },
        { concept: 'progressive', name: 'Estar + gerúndio', example: 'Estou comendo.' },
        { concept: 'directPron', name: 'Pronomes de objeto direto', example: 'Você vê o livro? Sim, eu o vejo.' },
        { concept: 'indirectPron', name: 'Pronomes de objeto indireto', example: 'Mandei-lhe um e-mail.' },
        { concept: 'prepositions', name: 'Preposições', example: 'Vou a São Paulo com a Ana.' },
        { concept: 'comparatives', name: 'Comparativos e superlativos', example: 'Ele é mais alto do que eu.' },
        { concept: 'imperative', name: 'Imperativo', example: 'Venha aqui!' },
        { concept: 'serEstar', name: 'Ser e estar', example: 'Ela é italiana, mas está em Lisboa.' },
        { concept: 'personalInfinitive', name: 'Infinitivo pessoal', example: 'É importante estudarmos todos os dias.' },
    ],
    english: [
        { concept: 'pastDone', name: 'Simple past', example: 'Yesterday I ate a sandwich.' },
        { concept: 'pastOngoing', name: 'Past continuous', example: 'I was cooking when you called.' },
        { concept: 'future', name: 'Future (will and going to)', example: 'Tomorrow I will go to work.' },
        { concept: 'conditional', name: 'Conditional (would)', example: 'I would like a coffee.' },
        { concept: 'ifClauses', name: 'If clauses', example: 'If it rains, I will stay home.' },
        { concept: 'progressive', name: 'Present continuous', example: 'I am eating.' },
        { concept: 'presentPerfect', name: 'Present perfect', example: 'I have lived here for three years.' },
        { concept: 'phrasalVerbs', name: 'Phrasal verbs', example: 'She gave up smoking last year.' },
        { concept: 'prepositions', name: 'Prepositions', example: 'I go to London with my friend.' },
        { concept: 'comparatives', name: 'Comparatives and superlatives', example: 'He is taller than me.' },
        { concept: 'imperative', name: 'Imperative', example: 'Come here!' },
    ],
};

function getGrammarTopics(targetLang, interfaceLang) {
    const list = GRAMMAR_BY_LANGUAGE[targetLang] || GRAMMAR_BY_LANGUAGE.italian;
    return list.map(item => ({
        id: item.concept,
        name: item.name,
        example: item.example,
        explanation: (CONCEPTS[item.concept] || {})[interfaceLang] || (CONCEPTS[item.concept] || {}).en || '',
    }));
}

function pickPrompt(interfaceLang, length, previous) {
    const bank = (PROMPTS[interfaceLang] || PROMPTS.en)[length === 'long' ? 'long' : 'short'];
    if (bank.length < 2) return bank[0];
    let next = bank[Math.floor(Math.random() * bank.length)];
    let guard = 0;
    while (next === previous && guard < 10) {
        next = bank[Math.floor(Math.random() * bank.length)];
        guard += 1;
    }
    return next;
}

export default function Sentences() {
    const navigate = useNavigate();
    const { t, interfaceLanguage } = useLanguage();
    const { activeLanguage, activeLevel } = useUserLanguage();

    const [mode, setMode] = useState(null);
    const [length, setLength] = useState(null);
    const [showGrammarPicker, setShowGrammarPicker] = useState(false);
    const [previewGrammar, setPreviewGrammar] = useState(null);
    const [chosenGrammar, setChosenGrammar] = useState(null);
    const [customGrammarInput, setCustomGrammarInput] = useState('');
    const [prompt, setPrompt] = useState('');
    const [text, setText] = useState('');
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState(null);

    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';

    const names = LANGUAGE_NAMES[uiLang];
    const targetName = names[targetLang] || names.italian;
    const grammarTopics = getGrammarTopics(targetLang, uiLang);

    const s = (key) => (TEXT[uiLang][key] || TEXT.en[key])
        .replace('{language}', targetName);

    const startTopicMode = (chosenLength) => {
        setMode('topic');
        setLength(chosenLength);
        setChosenGrammar(null);
        setResult(null);
        setText('');
        setPrompt(pickPrompt(uiLang, chosenLength, prompt));
    };

    const startGrammarMode = (grammarName, chosenLength) => {
        setMode('grammar');
        setLength(chosenLength);
        setChosenGrammar(grammarName);
        setShowGrammarPicker(false);
        setPreviewGrammar(null);
        setResult(null);
        setText('');
        const count = chosenLength === 'long' ? s('count5') : s('count2');
        setPrompt(s('grammarPrompt').replace('{count}', count).replace('{topic}', grammarName));
    };

    const submitText = async () => {
        if (!text.trim()) return;
        setChecking(true);
        try {
            const response = await fetch('/api/review-writing', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: text.trim(),
                    language: ENGLISH_NAMES[targetLang] || 'Italian',
                    interfaceLanguageName: ENGLISH_NAMES[uiLang] || 'English',
                    level,
                    grammarTopic: mode === 'grammar' ? chosenGrammar : null,
                    prompt: mode === 'topic' ? prompt : null,
                }),
            });
            if (!response.ok) throw new Error('failed');
            const data = await response.json();
            setResult(data);
            const issueCount = (data.issues || []).length;
            const xp = data.overallCorrect ? 20 : Math.max(5, 20 - issueCount * 3);
            awardXP(xp, 'sentences');
        } catch (err) {
            setResult({ error: true });
        }
        setChecking(false);
    };

    const tryAgain = () => {
        setResult(null);
    };

    const newPrompt = () => {
        if (mode === 'topic') {
            startTopicMode(length);
        } else {
            setResult(null);
            setText('');
        }
    };

    const backToStart = () => {
        setMode(null);
        setLength(null);
        setResult(null);
        setText('');
        setPrompt('');
    };

    if (!mode) {
        return (
            <div className="sn-screen">
                <div className="sn-top-bar">
                    <button className="sn-close" onClick={() => navigate('/home')}>✕</button>
                    <div className="sn-title">{t('home.sentences')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="sn-picker">
                    <div className="sn-picker-icon">✏️</div>
                    <div className="sn-picker-title">{s('pickerTitle')}</div>
                    <div className="sn-picker-sub">{s('pickerSub')}</div>

                    <div className="sn-mode-label">{s('chooseTopic')}</div>
                    <div className="sn-length-row">
                        <button className="sn-length-btn" onClick={() => startTopicMode('short')}>
                            {s('shortBtn')}
                        </button>
                        <button className="sn-length-btn" onClick={() => startTopicMode('long')}>
                            {s('longBtn')}
                        </button>
                    </div>

                    <div className="sn-mode-label">{s('orGrammar')}</div>
                    <button className="sn-mode-card" onClick={() => setShowGrammarPicker(true)}>
                        <div className="sn-mode-emoji">📐</div>
                        <div>
                            <div className="sn-mode-name">{s('grammarPractice')}</div>
                            <div className="sn-mode-desc">{s('grammarPracticeDesc')}</div>
                        </div>
                    </button>
                </div>

                {showGrammarPicker && (
                    <div className="sn-overlay" onClick={() => setShowGrammarPicker(false)}>
                        <div className="sn-sheet" onClick={e => e.stopPropagation()}>
                            <div className="sn-sheet-title">{s('chooseGrammar')}</div>
                            <div className="sn-custom-row">
                                <input
                                    className="sn-text-input-inline"
                                    type="text"
                                    placeholder={s('typeOwn')}
                                    value={customGrammarInput}
                                    onChange={e => setCustomGrammarInput(e.target.value)}
                                />
                            </div>
                            <div className="sn-grammar-list">
                                {grammarTopics.map(g => (
                                    <button key={g.id} className="sn-mode-card" onClick={() => setPreviewGrammar(g)}>
                                        <div className="sn-mode-emoji">📐</div>
                                        <div>
                                            <div className="sn-mode-name">{g.name}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                            {customGrammarInput.trim() && (
                                <div className="sn-length-row" style={{ marginTop: 12 }}>
                                    <button className="sn-length-btn" onClick={() => startGrammarMode(customGrammarInput.trim(), 'short')}>{s('short')}</button>
                                    <button className="sn-length-btn" onClick={() => startGrammarMode(customGrammarInput.trim(), 'long')}>{s('long')}</button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {previewGrammar && (
                    <div className="sn-overlay" onClick={() => setPreviewGrammar(null)}>
                        <div className="sn-sheet" onClick={e => e.stopPropagation()}>
                            <div className="sn-sheet-title">{previewGrammar.name}</div>
                            <div className="sn-explanation">
                                {previewGrammar.explanation}
                                {previewGrammar.example && (
                                    <>
                                        <br />
                                        {s('example')} “{previewGrammar.example}”
                                    </>
                                )}
                            </div>
                            <div className="sn-length-row">
                                <button className="sn-length-btn" onClick={() => startGrammarMode(previewGrammar.name, 'short')}>{s('short')}</button>
                                <button className="sn-length-btn" onClick={() => startGrammarMode(previewGrammar.name, 'long')}>{s('long')}</button>
                            </div>
                            <button className="sn-back-link" onClick={() => setPreviewGrammar(null)}>{s('chooseAnother')}</button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="sn-screen">
            <div className="sn-top-bar">
                <button className="sn-close" onClick={backToStart}>✕</button>
                <div className="sn-title">{t('home.sentences')}</div>
                <div className="sn-length-badge">{length === 'long' ? s('paragraph') : s('short')}</div>
            </div>

            <div className="sn-content">
                <div className="sn-prompt-card">
                    <div className="sn-prompt-label">{mode === 'grammar' ? s('grammarFocus') : s('yourPrompt')}</div>
                    <div className="sn-prompt-text">{prompt}</div>
                </div>

                {!result ? (
                    <>
                        <textarea
                            className="sn-textarea"
                            placeholder={s('placeholder')}
                            value={text}
                            onChange={e => setText(e.target.value)}
                            rows={length === 'long' ? 8 : 4}
                        />
                        <button className="sn-submit-btn" disabled={!text.trim() || checking} onClick={submitText}>
                            {checking ? s('checking') : s('check')}
                        </button>
                    </>
                ) : result.error ? (
                    <div className="sn-error-card">
                        {s('error')}
                        <button className="sn-retry-btn" onClick={tryAgain}>{s('tryAgainBtn')}</button>
                    </div>
                ) : (
                    <div className="sn-result">
                        <div className="sn-your-text">
                            <div className="sn-result-label">{s('youWrote')}</div>
                            <div className="sn-original-text">{text}</div>
                        </div>

                        {result.overallCorrect ? (
                            <div className="sn-all-correct">
                                {s('allCorrect')}
                            </div>
                        ) : (
                            <div className="sn-issues">
                                <div className="sn-result-label">{s('corrections')}</div>
                                {(result.issues || []).map((issue, i) => (
                                    <div key={i} className="sn-issue-card">
                                        <div className="sn-issue-wrong">"{issue.original}"</div>
                                        <div className="sn-issue-arrow">↓</div>
                                        <div className="sn-issue-correct">"{issue.corrected}"</div>
                                        <div className="sn-issue-explanation">{issue.explanation}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {result.strengths && result.strengths.length > 0 && (
                            <div className="sn-strengths">
                                <div className="sn-result-label">{s('didWell')}</div>
                                {result.strengths.map((item, i) => (
                                    <div key={i} className="sn-strength-row">✓ {item}</div>
                                ))}
                            </div>
                        )}

                        {result.encouragement && (
                            <div className="sn-encouragement">{result.encouragement}</div>
                        )}

                        <button className="sn-submit-btn" onClick={newPrompt}>
                            {mode === 'topic' ? s('newPrompt') : s('tryAgain')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}